import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card as MuiCard, CardActions, CardContent, CardHeader, cardHeaderClasses, Collapse, Divider, Icon, IconButton, Link, Menu, MenuItem, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import classnames from 'classnames';

import isFunction from 'Lib/utilities/isFunction';
import isEmpty from 'Lib/utilities/isEmpty';
import { Button } from '@mui/material';

const Actions = ({ actions, icon, tooltip, noBlank, onClick }) => {
	const [menuAnchorElement, setMenuAnchorElement] = useState(null);

	let action = null;
	let actionList = [];

	if (isFunction(actions)) {
		actionList = actions();
	} else {
		actionList = actions;
	}

	if (actionList && actionList.length == 1) {
		action = actionList[0];

		if (action.to) {
			return (
				<Link sx={{ display: 'flex' }} component={RouterLink} underline="hover" target={noBlank ? '_self' : '_blank'} to={action.to}>
					{action.label}
				</Link>
			);
		}

		if (isEmpty(icon)) {
			return (
				<Button sx={{ display: 'flex', fontSize: '16px' }} onClick={action.onClick} underline="hover">
					{action.label}
				</Button>
			);
		}

		return tooltip ? (
			<Tooltip title={tooltip} arrow placement="top">
				<IconButton onClick={action.onClick}>
					<Icon>{icon}</Icon>
				</IconButton>
			</Tooltip>
		) : (
			// this class is needed in order to allow for
			// dashboard widgets to be deleted/rearranged because of the
			// hierarchy of the draggable onclick handlers
			<span className="cancelSelectorName">
				<IconButton data-cy={icon} onClick={action.onClick}>
					{icon}
				</IconButton>
			</span>
		);
	} else if (actionList && actionList.length > 1) {
		return (
			<>
				<Tooltip title={tooltip} arrow placement="top">
					<div>
						<IconButton onClick={event => setMenuAnchorElement(event.currentTarget)} sx={{ mr: -2 }}>
							{icon}
						</IconButton>
					</div>
				</Tooltip>
				<Menu
					disablePortal
					anchorEl={menuAnchorElement}
					open={Boolean(menuAnchorElement)}
					onClose={() => setMenuAnchorElement(null)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left'
					}}>
					{actionList.map((action, index) => (
						<MenuItem
							key={index}
							onClick={() => {
								action.onClick();
								setMenuAnchorElement(null);
							}}>
							{action.label}
						</MenuItem>
					))}
				</Menu>
			</>
		);
	}
};

const Header = ({ forceDropdown, title, subheader, actions, tableSettings, tooltip, icon, renderActions, renderHeader, noBlank }) => {
	if (isFunction(renderHeader)) {
		return renderHeader();
	}

	const ActionComponent = () => (
		<div style={{ display: 'flex' }}>
			{tableSettings}
			{actions && <Actions forceDropdown={forceDropdown} noBlank={noBlank} icon={icon} actions={actions} tooltip={tooltip} renderActions={renderActions} />}
		</div>
	);

	return title || subheader || actions ? (
		<CardHeader
			data-cy={`card-title ${title}`}
			sx={theme => ({
				[`& .${cardHeaderClasses.title}`]: {
					display: 'inline-flex',
					fontSize: theme.typography.pxToRem(22)
				},
				[`& .${cardHeaderClasses.subheader}`]: {
					display: 'inline-flex',
					padding: '0 8px'
				},
				[`& .${cardHeaderClasses.action}`]: {
					alignSelf: 'center'
				},
				padding: '0 7px 8px 0'
			})}
			title={title}
			subheader={subheader}
			action={<ActionComponent />}
		/>
	) : null;
};

const CardFooter = ({ items, expandedContent, ...rest }) => {
	const [expanded, setExpanded] = useState(false);

	const handleExpandClick = () => {
		setExpanded(!expanded);
	};

	return (
		<Box {...rest}>
			<Divider sx={{ mx: -4 }} />
			<FooterContainer>
				<Box display="flex">
					{items.map(({ icon, content }, index) => (
						<FooterItem key={index}>
							{icon && <FooterIcon>{icon}</FooterIcon>}
							{content()}
						</FooterItem>
					))}
				</Box>
				<Box sx={{ ml: '0 !important' }}>
					{expandedContent && (
						<ExpandMore expand={expanded} onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
							<Icon>expand_more</Icon>
						</ExpandMore>
					)}
				</Box>
			</FooterContainer>
			{expandedContent && (
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					<CardContent>{expandedContent()}</CardContent>
				</Collapse>
			)}
		</Box>
	);
};

/**
 * Function component for rendering a card with header, children, and optional footer.
 *
 * @param {Object} children - The content to be displayed in the card.
 * @param {Object} sx - MUI custom styles to be applied to the card container.
 * @param {React.Component} Footer - Optional component to be displayed as the card footer.
 * @param {string} className - Additional CSS classes to be applied to the card.
 * @param {string} variant - Theme variants to be applied to the Card Container
 * @param {Object} rest - Additional props to be passed to the card.
 * @return {React.JSX.Element} The rendered Card component.
 */
export default function Card({ children, sx, Footer, variant, className, ...rest }) {
	const additionalClasses = className ? ' ' + className : '';

	return (
		<CardContainer sx={[...(Array.isArray(sx) ? sx : [sx])]} className={classnames({ 'empty-card': !children }) + additionalClasses} variant={variant}>
			<Header {...rest} />
			{children}
			{Footer ? <CardFooter {...Footer} /> : null}
		</CardContainer>
	);
}

const CardContainer = styled(MuiCard)(({ theme }) => ({
	width: '100%',
	borderRadius: '12px',
	border: '1px solid',
	padding: '15px',
	borderColor: theme.palette.common.lightgray,
	background: theme.palette.common.lightblue,
	boxShadow: 'none',
	display: 'flex',
	flexDirection: 'column'
}));

const FooterContainer = styled(CardActions)(() => ({
	display: 'flex',
	justifyContent: 'space-between',
	padding: '4px 0 0',
	marginRight: '-12px'
}));

const FooterItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	'&:not(:last-child)': {
		marginRight: theme.spacing(4)
	}
}));

const FooterIcon = styled(Icon)(({ theme }) => ({
	marginRight: theme.spacing(1)
}));

const ExpandMore = styled(props => {
	const { expand, ...other } = props;

	return <IconButton {...other} />;
})(({ theme, expand }) => ({
	transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest
	})
}));
